import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Dialog } from '../design/primitives/Overlay';
import { Kbd, keyLabel } from '../design/primitives/Misc';
import type { IconName } from '../design/icons';

// Keyboard-first: a tiny hotkey registry — and the ONE registration for a page's actions. A screen
// calls usePageActions([...]) once and each action shows up everywhere it can be reached: its key
// (when it has one), the ⌘K palette (`>` lists only actions), the `.` quick menu, and the `?` sheet.
// Bindings are strings like 'mod+k', '/', 'g i'
// (a chord: press g, then i within 800ms), '?' (shift+/ is normalised). Hotkeys never fire
// while typing in an input unless `global` is set. `?` opens the shortcut sheet.

export interface Hotkey {
  keys: string;
  run: (e: KeyboardEvent) => void;
  /** Human label for the sheet; omit to hide. */
  label?: string;
  group?: string;
  when?: () => boolean;
  /** Fire even inside inputs. */
  global?: boolean;
  /** Set for a page action (usePageActions): listed by the palette and the quick menu too. */
  pageAction?: { id: string; icon?: IconName };
}

/** One of the current page's actions (see usePageActions). `keys` is optional. */
export interface PageAction {
  id: string;
  label: string;
  run: () => void;
  keys?: string;
  icon?: IconName;
  group?: string;
  /** Hidden from every surface while this returns false (e.g. nothing selected). */
  when?: () => boolean;
}

interface Registry {
  list: Hotkey[];
  register: (h: Hotkey[]) => () => void;
}

const Ctx = createContext<Registry | null>(null);

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

function normalize(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.metaKey || e.ctrlKey) parts.push('mod');
  if (e.altKey) parts.push('alt');
  if (e.shiftKey && e.key.length > 1) parts.push('shift');
  let k = e.key;
  if (k === ' ') k = 'space';
  if (k.length === 1) k = k.toLowerCase();
  if (k === 'escape') k = 'esc';
  parts.push(k);
  return parts.join('+');
}

function isTyping(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  if (!t || typeof t.closest !== 'function') return false;
  const tag = t.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t.isContentEditable || Boolean(t.closest('[role="dialog"]')) || Boolean(t.closest('.hf-richtext-body'));
}

export function HotkeyProvider({ children }: { children: ReactNode }) {
  const [list, setList] = useState<Hotkey[]>([]);
  const pending = useRef<{ key: string; at: number } | null>(null);
  const listRef = useRef(list);
  listRef.current = list;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const combo = normalize(e);
      const typing = isTyping(e);
      const now = Date.now();
      const chordFirst = pending.current && now - pending.current.at < 800 ? pending.current.key : null;
      // Newest registrations win (screen-level over shell-level).
      for (let i = listRef.current.length - 1; i >= 0; i--) {
        const h = listRef.current[i]!;
        if (typing && !h.global) continue;
        if (h.when && !h.when()) continue;
        if (!h.keys) continue; // a page action without a shortcut
        const seq = h.keys.split(' ');
        if (seq.length === 2) {
          if (chordFirst === seq[0] && combo === seq[1]) {
            e.preventDefault();
            pending.current = null;
            h.run(e);
            return;
          }
          continue;
        }
        if (combo === seq[0]) {
          e.preventDefault();
          h.run(e);
          return;
        }
      }
      // Start a chord if any binding begins with this key.
      if (!typing && listRef.current.some((h) => h.keys.split(' ').length === 2 && h.keys.split(' ')[0] === combo)) pending.current = { key: combo, at: now };
      else pending.current = null;
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // `register` must be referentially stable: consumers re-register on it, and a fresh
  // function per list change would loop (register → setList → new register → …).
  const register = useCallback((hs: Hotkey[]) => {
    setList((l) => [...l, ...hs]);
    return () => setList((l) => l.filter((x) => !hs.includes(x)));
  }, []);
  const value = useMemo<Registry>(() => ({ list, register }), [list, register]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Register hotkeys for the lifetime of the component. Safe without a provider (no-op). */
export function useHotkeys(hotkeys: Hotkey[]) {
  const reg = useContext(Ctx);
  const ref = useRef(hotkeys);
  ref.current = hotkeys;
  const sig = hotkeys.map((h) => `${h.keys}:${h.label ?? ''}:${h.pageAction?.id ?? ''}`).join('|');
  useEffect(() => {
    if (!reg) return;
    // Register stable wrappers that call the latest handlers.
    const wrapped = ref.current.map((h, i) => ({ ...h, run: (e: KeyboardEvent) => ref.current[i]?.run(e), when: () => ref.current[i]?.when?.() ?? true }));
    return reg.register(wrapped);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, reg?.register]);
}

export function useHotkeyList(): Hotkey[] {
  return useContext(Ctx)?.list ?? [];
}

/** Register the current page's actions (one registration: keys, palette, quick menu, `?` sheet). */
export function usePageActions(actions: PageAction[]) {
  useHotkeys(actions.map((a) => ({ keys: a.keys ?? '', run: () => a.run(), label: a.label, group: a.group ?? 'This page', when: a.when, pageAction: { id: a.id, icon: a.icon } })));
}

/** The page actions currently registered (newest page first), deduplicated by id. */
export function usePageActionList(): Hotkey[] {
  const list = useHotkeyList();
  const seen = new Set<string>();
  const out: Hotkey[] = [];
  for (let i = list.length - 1; i >= 0; i--) {
    const h = list[i]!;
    if (!h.pageAction || seen.has(h.pageAction.id) || (h.when && !h.when())) continue;
    seen.add(h.pageAction.id);
    out.push(h);
  }
  return out;
}

export function ShortcutSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const list = useHotkeyList().filter((h) => h.label);
  const groups = new Map<string, Hotkey[]>();
  for (const h of list) {
    const g = h.group ?? 'General';
    if (!groups.has(g)) groups.set(g, []);
    if (!groups.get(g)!.some((x) => x.keys === h.keys && (h.keys !== '' || x.label === h.label))) groups.get(g)!.push(h);
  }
  return (
    <Dialog open={open} onClose={onClose} title="Keyboard shortcuts" description={IS_MAC ? 'Hold ⌘ where shown.' : 'Hold Ctrl where shown.'} size="lg">
      <div className="hf-shortcuts">
        {[...groups].map(([g, hs]) => (
          <section key={g}>
            <div className="hf-caps">{g}</div>
            <dl>
              {hs.map((h) => (
                <div key={`${h.keys}:${h.label}`}>
                  <dt>{h.label}</dt>
                  <dd>
                    {!h.keys ? <span className="hf-muted">⌘K or .</span> : h.keys.split(' ').map((k, i) => (
                      <span key={i}>
                        {i > 0 && <span className="hf-muted"> then </span>}
                        <Kbd>{keyLabel(k)}</Kbd>
                      </span>
                    ))}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </Dialog>
  );
}
